import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Workflow,
  TrendingUp,
  TrendingDown,
  Bot,
  Zap,
  Plus,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Loader2,
  Brain,
  Wallet,
  Activity,
  Sparkles,
  Flame,
  BarChart2,
  Clock,
  AlertTriangle,
  DollarSign,
  Volume2,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Bell,
  Target,
  Shield,
  Layers,
} from 'lucide-react';

// Types for REAL API data
interface MarketStats {
  totalMarketCap: number;
  totalVolume24h: number;
  btcDominance: number;
  ethDominance: number;
  activeCryptos: number;
  exchanges: number;
  marketCapChange24h: number;
}

interface FearGreedData {
  value: number;
  classification: string;
  timestamp: number;
}

interface GainerLoser {
  symbol: string;
  pair: string;
  price: number;
  priceChange24h: number;
  volume24h: number;
  quoteVolume24h: number;
}

interface DashboardData {
  stats: MarketStats | null;
  fearGreed: FearGreedData | null;
  trending: any[];
  gainers: GainerLoser[];
  losers: GainerLoser[];
}

interface Signal {
  id: number;
  type: string;
  symbol: string;
  message: string;
  time: string;
  severity: 'high' | 'medium' | 'low';
}

// Signals will be populated by user alerts/triggers - currently empty
// This is NOT mock data - signals are user-generated
const recentSignals: Signal[] = [];

export default function Dashboard() {
  const [fearGreedAngle, setFearGreedAngle] = useState(0);

  // Fetch REAL dashboard data from API
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard-data'],
    queryFn: () => api.get('/market/dashboard').then((r) => r.data),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: workflows } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api.get('/workflows').then((r: { data: any }) => r.data),
  });

  const { data: executions } = useQuery({
    queryKey: ['recent-executions'],
    queryFn: () => api.get('/executions?limit=5').then((r: { data: any }) => r.data),
  });

  const { data: bots } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.get('/bots').then((r: { data: any }) => r.data),
  });

  const { data: tickers } = useQuery({
    queryKey: ['tickers'],
    queryFn: () => api.get('/trading/tickers').then((r: { data: any }) => r.data),
    refetchInterval: 5000,
  });

  // Extract real data from API response
  const marketStats = dashboardData?.stats;
  const fearGreed = dashboardData?.fearGreed;
  const topGainers = dashboardData?.gainers || [];
  const topLosers = dashboardData?.losers || [];

  useEffect(() => {
    // Animate fear/greed gauge with REAL data
    if (fearGreed?.value) {
      const timer = setTimeout(() => {
        setFearGreedAngle(fearGreed.value * 1.8); // 0-100 to 0-180 degrees
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fearGreed]);

  const botsArray = Array.isArray(bots) ? bots : (bots?.bots || []);
  const executionsArray = Array.isArray(executions) ? executions : (executions?.executions || []);
  const runningBots = botsArray.filter((b: any) => b.status === 'running').length;
  const totalWorkflows = workflows?.workflows?.length || 0;
  const successfulExecutions = executionsArray.filter((e: any) => e.status === 'completed').length;

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-danger" />;
      case 'running':
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      default:
        return <Activity className="h-4 w-4 text-text-tertiary" />;
    }
  };

  const formatVolume = (vol: number) => {
    if (vol >= 1e9) return '$' + (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return '$' + (vol / 1e6).toFixed(2) + 'M';
    return '$' + (vol / 1e3).toFixed(2) + 'K';
  };

  const formatMarketCap = (cap: number) => {
    return '$' + (cap / 1e12).toFixed(2) + 'T';
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Hero Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-primary">Dashboard</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">Welcome back</h1>
            <p className="text-text-secondary">Here's what's happening with your platform today.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/alerts" className="btn-secondary flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Alerts
            </Link>
            <Link to="/workflow/new" className="btn-primary flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Workflow
            </Link>
          </div>
        </div>
      </div>

      {/* Global Market Stats Bar - REAL DATA from CoinGecko */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        <div className="card px-4 py-3 flex items-center gap-3">
          <Globe className="h-5 w-5 text-[#8b949e]" />
          <div>
            <div className="text-xs text-[#8b949e]">Market Cap</div>
            <div className="font-bold text-white">
              {marketStats ? formatMarketCap(marketStats.totalMarketCap) : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
        <div className="card px-4 py-3 flex items-center gap-3">
          <Volume2 className="h-5 w-5 text-[#8b949e]" />
          <div>
            <div className="text-xs text-[#8b949e]">24h Volume</div>
            <div className="font-bold text-white">
              {marketStats ? formatVolume(marketStats.totalVolume24h) : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
        <div className="card px-4 py-3 flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
            <span className="text-[10px] font-bold text-[#F7931A]">₿</span>
          </div>
          <div>
            <div className="text-xs text-[#8b949e]">BTC Dom</div>
            <div className="font-bold text-white">
              {marketStats ? `${marketStats.btcDominance.toFixed(1)}%` : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
        <div className="card px-4 py-3 flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[#627EEA]/20 flex items-center justify-center">
            <span className="text-[10px] font-bold text-[#627EEA]">Ξ</span>
          </div>
          <div>
            <div className="text-xs text-[#8b949e]">ETH Dom</div>
            <div className="font-bold text-white">
              {marketStats ? `${marketStats.ethDominance.toFixed(1)}%` : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
        <div className="card px-4 py-3 flex items-center gap-3">
          <Layers className="h-5 w-5 text-[#8b949e]" />
          <div>
            <div className="text-xs text-[#8b949e]">Coins</div>
            <div className="font-bold text-white">
              {marketStats ? marketStats.activeCryptos.toLocaleString() : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
        <div className="card px-4 py-3 flex items-center gap-3">
          <BarChart2 className="h-5 w-5 text-[#8b949e]" />
          <div>
            <div className="text-xs text-[#8b949e]">Exchanges</div>
            <div className="font-bold text-white">
              {marketStats ? marketStats.exchanges.toLocaleString() : dashboardLoading ? '...' : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Trending & Market */}
        <div className="col-span-8 space-y-6">
          {/* Trending & Top Losers Row */}
          <div className="grid grid-cols-2 gap-6">
            {/* Top Gainers - REAL DATA from Binance */}
            <div className="card">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-[#00D4AA]" />
                  <h2 className="font-heading text-lg font-semibold text-text-primary">Top Gainers</h2>
                </div>
                <Link to="/scanner?preset=gainers" className="text-sm text-primary hover:text-primary-hover flex items-center gap-1 transition-colors">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="p-3">
                {topGainers.length === 0 && dashboardLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}
                {topGainers.length === 0 && !dashboardLoading && (
                  <div className="text-center py-8 text-[#8b949e] text-sm">No data available</div>
                )}
                {topGainers.map((coin, i) => (
                  <Link
                    key={coin.symbol}
                    to={`/trade?pair=${coin.pair}`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-white/[0.03] transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#8b949e] w-4">{i + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{coin.symbol.slice(0, 2)}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white text-sm">{coin.symbol}</span>
                          {i < 2 && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#00D4AA]/20 text-[#00D4AA]">HOT</span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#8b949e]">{formatVolume(coin.quoteVolume24h)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-white">${coin.price < 0.01 ? coin.price.toFixed(8) : coin.price.toFixed(2)}</div>
                      <div className="flex items-center justify-end gap-1 text-xs text-[#00D4AA]">
                        <ArrowUpRight className="h-3 w-3" />
                        +{coin.priceChange24h.toFixed(2)}%
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Top Losers - REAL DATA from Binance */}
            <div className="card">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-[#FF6B6B]" />
                  <h2 className="font-heading text-lg font-semibold text-text-primary">Top Losers</h2>
                </div>
                <Link to="/scanner?preset=losers" className="text-sm text-primary hover:text-primary-hover flex items-center gap-1 transition-colors">
                  View all <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="p-3">
                {topLosers.length === 0 && dashboardLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}
                {topLosers.length === 0 && !dashboardLoading && (
                  <div className="text-center py-8 text-[#8b949e] text-sm">No data available</div>
                )}
                {topLosers.map((coin, i) => (
                  <Link
                    key={coin.symbol}
                    to={`/trade?pair=${coin.pair}`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-white/[0.03] transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#8b949e] w-4">{i + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B6B]/30 to-[#EF4444]/30 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{coin.symbol.slice(0, 2)}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-white text-sm">{coin.symbol}</span>
                        <div className="text-[10px] text-[#8b949e]">{formatVolume(coin.quoteVolume24h)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-white">${coin.price < 0.01 ? coin.price.toFixed(8) : coin.price.toFixed(2)}</div>
                      <div className="flex items-center justify-end gap-1 text-xs text-[#FF6B6B]">
                        <ArrowDownRight className="h-3 w-3" />
                        {coin.priceChange24h.toFixed(2)}%
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="stat-card group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Workflow className="h-6 w-6 text-primary" />
                </div>
                <span className="badge-success badge-dot">Active</span>
              </div>
              <div className="font-heading text-3xl font-bold text-text-primary mb-1">{totalWorkflows}</div>
              <div className="text-sm text-text-secondary">Total Workflows</div>
            </div>

            <div className="stat-card group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center group-hover:bg-success/20 transition-colors">
                  <Bot className="h-6 w-6 text-success" />
                </div>
                <span className="badge-success badge-dot">{runningBots} Running</span>
              </div>
              <div className="font-heading text-3xl font-bold text-text-primary mb-1">{bots?.length || 0}</div>
              <div className="text-sm text-text-secondary">Trading Bots</div>
            </div>

            <div className="stat-card group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <Zap className="h-6 w-6 text-accent" />
                </div>
              </div>
              <div className="font-heading text-3xl font-bold text-text-primary mb-1">{successfulExecutions}</div>
              <div className="text-sm text-text-secondary">Executions Today</div>
            </div>

            <div className="stat-card group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center group-hover:bg-info/20 transition-colors">
                  <Brain className="h-6 w-6 text-info" />
                </div>
              </div>
              <div className="font-heading text-3xl font-bold text-text-primary mb-1">3</div>
              <div className="text-sm text-text-secondary">AI Models Ready</div>
            </div>
          </div>

          {/* Market Overview */}
          <div className="card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <h2 className="font-heading text-lg font-semibold text-text-primary">Market Overview</h2>
              <Link to="/trade" className="text-sm text-primary hover:text-primary-hover flex items-center gap-1 transition-colors">
                Trade <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="p-5">
              <div className="space-y-2">
                {tickers?.slice(0, 4).map((ticker: any, index: number) => (
                  <Link
                    key={ticker.pair}
                    to={`/trade/${ticker.pair.toLowerCase()}`}
                    className="flex items-center justify-between p-4 rounded-xl hover:bg-white/[0.03] transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-background-primary font-bold text-sm group-hover:scale-105 transition-transform">
                        {ticker.pair.split('-')[0].slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-semibold text-text-primary">{ticker.pair}</div>
                        <div className="text-xs text-text-tertiary">Spot</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-text-primary font-mono">
                        ${ticker.lastPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                      <div className={cn(
                        "text-sm font-medium flex items-center justify-end gap-1",
                        ticker.changePercent24h >= 0 ? "text-success" : "text-danger"
                      )}>
                        {ticker.changePercent24h >= 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {ticker.changePercent24h >= 0 ? '+' : ''}{ticker.changePercent24h?.toFixed(2)}%
                      </div>
                    </div>
                  </Link>
                )) || (
                  <div className="text-center py-8 text-text-secondary">
                    No market data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="col-span-4 space-y-6">
          {/* Fear & Greed Index - REAL DATA from Alternative.me */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-[#8b949e]" />
              <h3 className="font-semibold text-white">Fear & Greed Index</h3>
            </div>
            <div className="relative h-32 flex items-center justify-center mb-4">
              <svg viewBox="0 0 200 100" className="w-full max-w-[200px]">
                {/* Background arc */}
                <path
                  d="M 20 90 A 70 70 0 0 1 180 90"
                  fill="none"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="12"
                  strokeLinecap="round"
                />
                {/* Gradient arc */}
                <defs>
                  <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="25%" stopColor="#F59E0B" />
                    <stop offset="50%" stopColor="#EAB308" />
                    <stop offset="75%" stopColor="#84CC16" />
                    <stop offset="100%" stopColor="#10B981" />
                  </linearGradient>
                </defs>
                <path
                  d="M 20 90 A 70 70 0 0 1 180 90"
                  fill="none"
                  stroke="url(#gaugeGradient)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray="220"
                  strokeDashoffset={220 - (220 * (fearGreed?.value || 0) / 100)}
                  style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                />
                {/* Needle */}
                <g transform={`rotate(${-90 + fearGreedAngle}, 100, 90)`} style={{ transition: 'transform 1s ease-out' }}>
                  <line x1="100" y1="90" x2="100" y2="35" stroke="white" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="100" cy="90" r="6" fill="white" />
                </g>
              </svg>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-1">
                {fearGreed?.value ?? (dashboardLoading ? '...' : '--')}
              </div>
              <div className={cn(
                "text-sm font-medium",
                (fearGreed?.value || 50) > 60 ? "text-[#10B981]" :
                (fearGreed?.value || 50) < 40 ? "text-[#EF4444]" :
                "text-[#F59E0B]"
              )}>
                {fearGreed?.classification || (dashboardLoading ? 'Loading...' : 'Unknown')}
              </div>
            </div>
            <div className="flex justify-between text-xs text-[#8b949e] mt-4 px-2">
              <span>Extreme Fear</span>
              <span>Extreme Greed</span>
            </div>
          </div>

          {/* Live Signals */}
          <div className="card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#00D4AA] animate-pulse" />
                <h3 className="font-semibold text-white">Live Signals</h3>
              </div>
              <Link to="/alerts" className="text-xs text-primary">View all</Link>
            </div>
            <div className="p-3 space-y-2">
              {recentSignals.length === 0 && (
                <div className="text-center py-6 text-[#8b949e] text-sm">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No signals yet</p>
                  <p className="text-xs mt-1">Configure alerts to see signals here</p>
                </div>
              )}
              {recentSignals.map(signal => (
                <div 
                  key={signal.id}
                  className={cn(
                    "p-3 rounded-xl border",
                    signal.severity === 'high' 
                      ? "bg-[#FF6B6B]/5 border-[#FF6B6B]/20" 
                      : "bg-white/[0.02] border-white/[0.06]"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      {signal.severity === 'high' && (
                        <AlertTriangle className="h-4 w-4 text-[#FF6B6B] mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-primary text-sm">{signal.symbol}</span>
                          <span className="text-[10px] text-[#8b949e] px-1.5 py-0.5 rounded bg-white/[0.04] capitalize">
                            {signal.type}
                          </span>
                        </div>
                        <p className="text-xs text-[#8b949e] mt-0.5">{signal.message}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-[#8b949e] flex-shrink-0">{signal.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="px-5 py-4 border-b border-white/[0.06]">
              <h2 className="font-heading text-lg font-semibold text-text-primary">Quick Actions</h2>
            </div>
            <div className="p-4 space-y-2">
              <Link
                to="/workflow/new"
                className="flex items-center gap-3 p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-all duration-200 group"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Plus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">New Workflow</div>
                  <div className="text-xs text-text-tertiary">Create automation</div>
                </div>
              </Link>

              <Link
                to="/bots"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-success/10 transition-all duration-200 group"
              >
                <div className="w-11 h-11 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Bot className="h-5 w-5 text-success" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">Create Bot</div>
                  <div className="text-xs text-text-tertiary">Automated trading</div>
                </div>
              </Link>

              <Link
                to="/scanner"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/10 transition-all duration-200 group"
              >
                <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Activity className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">Market Scanner</div>
                  <div className="text-xs text-text-tertiary">Find opportunities</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Active Bots */}
          <div className="card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <h2 className="font-heading text-lg font-semibold text-text-primary">Active Bots</h2>
              <Link to="/bots" className="text-sm text-primary hover:text-primary-hover transition-colors">
                View all
              </Link>
            </div>
            <div className="p-4">
              {botsArray.filter((b: any) => b.status === 'running').slice(0, 3).map((bot: any) => (
                <div key={bot.id} className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
                    <div>
                      <div className="font-medium text-text-primary text-sm">{bot.name}</div>
                      <div className="text-xs text-text-tertiary">{bot.pair}</div>
                    </div>
                  </div>
                  <span className="badge-success">{bot.type}</span>
                </div>
              )) || (
                <div className="text-center py-6 text-text-secondary text-sm">
                  No active bots
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Executions */}
      <div className="mt-6">
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
            <h2 className="font-heading text-lg font-semibold text-text-primary">Recent Workflow Executions</h2>
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Activity className="h-4 w-4 text-accent" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Duration</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {executions?.slice(0, 5).map((exec: any) => (
                  <tr key={exec.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Workflow className="h-4 w-4 text-text-tertiary" />
                        <span className="text-text-primary font-medium">{exec.workflow?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {statusIcon(exec.status)}
                        <span className={cn(
                          "capitalize font-medium",
                          exec.status === 'completed' && "text-success",
                          exec.status === 'failed' && "text-danger",
                          exec.status === 'running' && "text-primary"
                        )}>
                          {exec.status}
                        </span>
                      </div>
                    </td>
                    <td className="text-text-secondary capitalize">{exec.trigger || 'manual'}</td>
                    <td className="text-text-secondary font-mono text-xs">
                      {exec.duration ? `${exec.duration}ms` : '-'}
                    </td>
                    <td className="text-text-secondary text-sm">
                      {new Date(exec.createdAt).toLocaleString()}
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-text-secondary">
                      No recent executions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
